import React, {useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import './InfoTooltip.css';

/**
 * InfoTooltip Component
 * Displays a blue circle with white "i" that shows a tooltip message on hover
 * The tooltip content is rendered in a portal so it can appear above modal shells.
 *
 * @param {string} message - The tooltip message to display
 * @param {string} className - Optional additional CSS class
 */
const InfoTooltip = ({message, className = ''}) => {
  const iconRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({top: 0, left: 0});
  const [portalNode, setPortalNode] = useState(null);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const node = document.createElement('div');
    node.className = 'info-tooltip-portal';
    document.body.appendChild(node);
    setPortalNode(node);
    return () => {
      document.body.removeChild(node);
    };
  }, []);

  const updatePosition = () => {
    if (!iconRef.current || typeof window === 'undefined') return;
    const rect = iconRef.current.getBoundingClientRect();
    const tooltipLeft = Math.min(rect.right + 10, window.innerWidth - 360);
    const tooltipTop = Math.min(
      Math.max(rect.top + rect.height / 2, 16),
      window.innerHeight - 16
    );
    setPosition({top: tooltipTop, left: tooltipLeft});
  };

  useEffect(() => {
    if (!isVisible) return undefined;

    const handleReposition = () => updatePosition();
    // Recalculate placement if the user scrolls inside the modal or resizes
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [isVisible]);

  const showTooltip = () => {
    updatePosition();
    setIsVisible(true);
  };

  const hideTooltip = () => setIsVisible(false);

  const tooltipContent = (
    <span
      className="info-tooltip-text info-tooltip-text--floating"
      style={{top: `${position.top}px`, left: `${position.left}px`}}
      role="tooltip"
    >
      {message}
    </span>
  );

  return (
    <>
      <span
        ref={iconRef}
        className={`info-tooltip-container ${className}`}
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
      >
        <span
          className="info-icon"
          role="button"
          tabIndex={0}
          aria-label="Show additional information"
          onFocus={showTooltip}
          onBlur={hideTooltip}
        >
          i
        </span>
        {!portalNode && (
          <span className="info-tooltip-text info-tooltip-text--inline">
            {message}
          </span>
        )}
      </span>
      {portalNode && isVisible && createPortal(tooltipContent, portalNode)}
    </>
  );
};

export default InfoTooltip;
